import React, { useEffect, useRef, useState } from 'react';
import loadJs from 'load-js';
import { useLifecycles } from 'react-use';
import CircularProgress from '@mui/material/CircularProgress';
import { Alert, Box } from '@mui/material';
import { useSelector } from 'react-redux';

import { MAP_CLIENT_ID, SITE_URL } from '../../circulusConfig';

let navermap;

const SupplyMap = () => {
  const {
    loading0: loading,
    robots: data,
    refresh,
    status: { useRobots },
  } = useSelector((state) => state.dashboard);
  const mapEl = useRef(null);
  const [map, setMap] = useState(null);
  const [addLoading, setLoading] = useState(false);
  const [permission, setPermission] = useState(null);
  const [failed, setFailed] = useState(false);

  let markers = [];
  let loadScriptPromise = null;
  let isSubscribed = true;

  const loadNavermapsScript = () => {
    if (loadScriptPromise) {
      return loadScriptPromise;
    }

    loadScriptPromise = loadJs(
      `https://oapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${MAP_CLIENT_ID}&submodules=geocoder`,
    ).then(() => {
      const navermaps = window.naver.maps;

      if (navermaps.jsContentLoaded) {
        return navermaps;
      }

      const loadingJsContent = new Promise((resolve) => {
        navermaps.onJSContentLoaded = () => {
          resolve(navermaps);
        };
      });

      return loadingJsContent;
    });

    return loadScriptPromise;
  };

  const onError = () => {
    setLoading(false);
  };

  const getGeoPermission = () => {
    const { navigator } = window;
    if ('geolocation' in navigator) {
      return navigator.geolocation.getCurrentPosition((position) => {
        setPermission(!!position);
      }, onError);
    }
    return setPermission(false);
  };
  const onScriptError = () => {
    setLoading(false);
    setFailed('지도 스크립트 로드 중 오류가 발생했습니다.');
  };

  useEffect(() => {
    setFailed(false);
    if (navermap) {
      if (map) {
        const center = { lat: 0, lng: 0 };
        markers = data.map(({ geo, serial, name }, i) => {
          let marker;

          if (useRobots.includes(serial)) {
            marker = new navermap.Marker({
              position: new navermap.LatLng(geo.lat, geo.lng),
              title: `${name}(${serial})`,
              map,
              zIndex: `${i + 100}`,
            });
          } else {
            marker = new navermap.Marker({
              position: new navermap.LatLng(geo.lat, geo.lng),
              map,
              zIndex: 0,
              title: `${name}(${serial})`,
              icon: {
                url: `${SITE_URL}image/marker_offline.png`,
                size: new navermap.Size(25, 34),
                scaledSize: new navermap.Size(25, 34),
                origin: new navermap.Point(0, 0),
                anchor: new navermap.Point(12, 34),
              },
            });
          }

          center.lat += geo.lat;
          center.lng += geo.lng;
          return marker;
        });
      }
    }
  }, [map, useRobots]);

  useEffect(() => {
    if (navermap) {
      const center = { lat: 0, lng: 0 };
      data.forEach(({ geo }) => {
        center.lat += geo.lat;
        center.lng += geo.lng;
      });
      if (!map) {
        const m = new navermap.Map(mapEl.current, {
          useStyleMap: true,
          center: new navermap.LatLng(
            center.lat / data.length,
            center.lng / data.length,
          ),
          zoom: 5,
          zoomControl: true,
          zoomControlOptions: {
            style: navermap.ZoomControlStyle.SMALL,
          },
          minZoom: 1,
        });
        setMap(m);
      } else {
        map.setCenter(
          new navermap.LatLng(
            center.lat / data.length,
            center.lng / data.length,
          ),
        );
        map.setZoom(10, true);
      }
    }
  }, [navermap, data]);

  useEffect(() => {
    if (refresh) {
      for (let i = 0, ii = markers.length; i < ii; ) {
        markers[i].setMap(null);
        i += 1;
      }

      markers = [];
    }
  }, [refresh]);

  useLifecycles(
    () => {
      setLoading(true);
      if (isSubscribed && !loading) {
        loadNavermapsScript()
          .then(() => {
            setLoading(false);

            navermap = window.naver && window.naver.maps;
            getGeoPermission();
            const m = new navermap.Map(mapEl.current, {
              useStyleMap: true,
              center: new navermap.LatLng(37.3614483, 127.1114883),
              zoom: 5,
              zoomControl: true,
              zoomControlOptions: {
                style: navermap.ZoomControlStyle.SMALL,
              },
              minZoom: 1,
            });
            setMap(m);
            // 여기까지 세팅 끝
          })
          .catch(onScriptError);
      }
    },
    () => {
      isSubscribed = false;
    },
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {addLoading || (loading && <CircularProgress color="secondary" />)}
      {!(addLoading || loading) && (
        <>
          {failed ||
            (!permission && (
              <Alert color="warning">
                {failed || '위치 권한을 허용해주세요.'}
              </Alert>
            ))}
          <div ref={mapEl} style={{ width: '100%', height: '100%' }} />
        </>
      )}
    </Box>
  );
};

export default SupplyMap;
